import React, { ReactNode } from 'react';
import { BreadCrumbs, BreadCrumbsItemProps } from './BreadCrumbs';
import { TooltipWrapper } from '../TooltipWrapper';
import { MachineStatusIconStatusProps } from '../MachineStatusIconV2';
import { BreadCrumbsPaddingFixer } from './FleetBreadCrumbs.elements';
import { useTranslation } from 'react-i18next';
import { MachineStatusIndicator } from 'components/MachineStatus/MachineStatusIndicator';
import ArrowLeft from 'icons/indexed-icons/ArrowLeft';
import { themeColors } from 'themes';
import { useMediaQuery } from 'react-responsive'
import Setting from 'icons/indexed-icons/Setting';
import { useHistory } from 'react-router';


export interface FleetBreadCrumbsPathsProps {
  /** for the first level (customer name) fleet/x */
  customer?: BreadCrumbsItemProps;
  /** second level fleet/site/x */
  site?: BreadCrumbsItemProps;
  /** third level fleet/line/x */
  line?: BreadCrumbsItemProps;
  /** fourth level fleet/machine/x */
  machine?: BreadCrumbsItemProps;
}

const baseClass = 'bread-crumbs';

// Provide a button with the pencil icon and also a tooltip
const EditButton = ({
  handle,
  isLoading
}: {
  handle: () => void;
  isLoading?: boolean;
}): JSX.Element => {
  const loaded = !isLoading ? true : false;

  let className = `${baseClass}__edit`;

  if (!loaded) className = `${className} ${className}--is-loading`;

  return (
    <TooltipWrapper Tooltip="Configure Machine" bottom>
      <button
        disabled={!loaded}
        type="button"
        className={className}
        onClick={() => loaded && handle()}
      >
        <Setting />
        <span className="sr-only">edit settings</span>
      </button>
    </TooltipWrapper>
  );
};

export interface FleetBreadCrumbsProps {
  machineStatus?: MachineStatusIconStatusProps;
  handleEdit?: () => void;
  plantId?: string;
  paths?: FleetBreadCrumbsPathsProps;
  children?: ReactNode | ReactNode[];
  /** shows the machine details bar that contains the status, s/n, and order number */
  showMachineDetails?: boolean;
  needsPadding?: boolean;
  /** is this a config page */
  isConfig?: boolean;
  /** will this have a back button (arrow) */
  hideBackArrow?: boolean;
  isDisconnected?: boolean;
  businessUnit?: string;
  productionState?: string;
}

export const FleetBreadCrumbs = ({
  machineStatus,
  handleEdit,
  paths,
  needsPadding,
  isConfig,
  hideBackArrow,
  isDisconnected,
  businessUnit,
  productionState,
  ...rest
}: FleetBreadCrumbsProps): JSX.Element => {
  const pathItems = [];

  const { t } = useTranslation(['mh']);
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' })
  const history = useHistory();

  if (!paths) return <></>;

  if (paths.customer)
    pathItems.push({
      ...paths.customer,
      pathLabel: t('retrieving_customer')
    } as BreadCrumbsItemProps);
  if (paths.site)
    pathItems.push({ ...paths.site, pathLabel: t('retrieving_site') } as BreadCrumbsItemProps);
  if (paths.line)
    pathItems.push({ ...paths.line, pathLabel: t('retrieving_line') } as BreadCrumbsItemProps);
  if (paths.machine)
    pathItems.push({
      ...paths.machine,
      pathLabel: t('retrieving_machine')
    } as BreadCrumbsItemProps);
  if (isConfig) pathItems.push({ label: t('config'), pathLabel: t('config') });

  // Updating the Before const to handle navigation to previosu page for Mobile and Tablets
  const Before = isTabletOrMobile ? (hideBackArrow ? undefined : 
    <a href="#" onClick={history.goBack} className={`${baseClass}__btn-back`}>
      <ArrowLeft color={themeColors.textLightBlack} />
    </a>
  ):(hideBackArrow ? undefined : 
    <a href="/fleet" className={`${baseClass}__btn-back`}>
      <ArrowLeft color={themeColors.textLightBlack} />
    </a>
  );


  const machineStatusSettings = {
    machineStatus: productionState || machineStatus?.watchDog,
    lastConnected: machineStatus?.lastConnected,
    isLoading: machineStatus?.isLoading,
    isDisconnected,
    businessUnit: businessUnit
  };

  const CurrentBreadCrumbs = (
    <BreadCrumbs {...rest} items={pathItems} Before={Before}>
      {machineStatus && <MachineStatusIndicator {...{ ...machineStatusSettings }} />}
      {handleEdit && <EditButton handle={handleEdit} />}
    </BreadCrumbs>
  );

  // needsPadding is a fix for incorrectly coded pages, this should be removed when site is cleaned up
  if (needsPadding) return <BreadCrumbsPaddingFixer>{CurrentBreadCrumbs}</BreadCrumbsPaddingFixer>;
  else return CurrentBreadCrumbs;
};
